// Change password (iOS ChangePasswordSheet): a new password for the signed-in account with the live policy
// checklist. This browser stays signed in. Unlike the sign-up save, "same password" is an error here: the person
// asked for a new one. Opens in place from the Account screen's "Change password" button.

import { Check, KeyRound, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { teamErrorMessage } from '@/auth/errors';
import { changePassword } from '@/auth/flows';
import { isValidPassword } from '@/auth/passwordPolicy';
import { PasswordChecklist } from '@/features/auth/components/PasswordChecklist';
import { Caption, ErrorText, Field, PasswordInput, PrimaryButton, SecondaryButton } from '@/features/auth/components/ui';

export function ChangePasswordCard({ account, disabled }: { account: string | null; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = isValidPassword(password) && confirmation === password && !saving && !saved;

  // A beat for the checkmark before the form folds away (iOS dismisses the sheet after 0.45 s).
  useEffect(() => {
    if (!saved) return undefined;
    const timer = window.setTimeout(() => {
      setOpen(false);
      setSaved(false);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [saved]);

  const close = () => {
    setOpen(false);
    setPassword('');
    setConfirmation('');
    setRevealed(false);
    setError(null);
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await changePassword(password);
      setPassword('');
      setConfirmation('');
      setRevealed(false);
      setSaved(true);
    } catch (e) {
      setError(teamErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <SecondaryButton icon={<KeyRound className="h-4 w-4" aria-hidden />} disabled={disabled} onClick={() => setOpen(true)}>
        Change password
      </SecondaryButton>
    );
  }

  return (
    <form
      noValidate
      className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-700 dark:bg-slate-800/40"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div>
        <h3 className="text-base font-bold text-[var(--gt-navy)] dark:text-slate-50">Change your password</h3>
        <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
          For {account ?? 'your account'}. You stay signed in on this browser.
        </p>
      </div>
      <Field label="New password">
        {(id) => (
          <PasswordInput
            id={id}
            revealed={revealed}
            onToggleReveal={() => setRevealed((r) => !r)}
            autoComplete="new-password"
            placeholder="New password"
            value={password}
            disabled={saving}
            autoFocus
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </Field>
      <Field label="Confirm password">
        {(id) => (
          <PasswordInput
            id={id}
            revealed={revealed}
            showsRevealButton={false}
            autoComplete="new-password"
            placeholder="Confirm password"
            value={confirmation}
            disabled={saving}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        )}
      </Field>
      <PasswordChecklist password={password} confirmation={confirmation} />
      <ErrorText>{error}</ErrorText>
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        <SecondaryButton onClick={close} disabled={saving}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="submit"
          busy={saving}
          busyLabel="Saving…"
          icon={saved ? <Check className="h-4 w-4" aria-hidden /> : <ShieldCheck className="h-4 w-4" aria-hidden />}
          disabled={!canSave}
        >
          {saved ? 'Saved' : 'Save password'}
        </PrimaryButton>
      </div>
      <Caption>Other devices signed in to this account need the new password the next time they sign in.</Caption>
    </form>
  );
}
